import { create } from 'zustand';
import { NotificationApi } from '@app/api';
import { Notification, NotificationEnvelope, notificationFromEnvelope } from '@app/model/Notification';
import { NOTIFICATION_PAGE_SIZE } from '@app/utils/config';

interface NotificationStore {
  /** The pages loaded so far, newest first. Never holds anything the caller has dismissed. */
  items: Notification[];
  /**
   * Unread notifications in the whole inbox, not just in `items`. Answered by the service, because
   * the panel holds a page and the badge has to be right about the rest.
   */
  unreadCount: number;
  isLoaded: boolean;
  isLoading: boolean;
  /** Set when a load fails, so the panel can say so instead of looking empty. */
  error: string | null;
  /** Whether the last page came back full, which is the only evidence there may be another. */
  hasMore: boolean;
  /**
   * Bumped once per notification that arrives over the websocket. The bell watches it to play its
   * animation; a count rather than a flag so two arrivals in a row are two events, and so nothing
   * has to reset it.
   */
  arrivalToken: number;
  /**
   * The notification behind the most recent `arrivalToken`, for the toast layer to show.
   *
   * Held rather than derived from `items[0]`, because those are two different questions: the head
   * of the list is simply the newest notification and is filled by every load, while this is only
   * ever set by a push. Toasting the head would greet a user with a stack of alerts for everything
   * already in their inbox each time they sign in.
   */
  lastArrival: Notification | null;

  load: () => Promise<void>;
  loadMore: () => Promise<void>;
  catchUp: () => Promise<void>;
  receive: (envelope: NotificationEnvelope) => void;
  markRead: (id: number) => Promise<void>;
  markUnread: (id: number) => Promise<void>;
  markAllRead: () => Promise<void>;
  dismiss: (id: number) => Promise<void>;
  dismissAll: () => Promise<void>;
  reset: () => void;
}

/**
 * How many pages `dismissAll` will work through before giving up.
 *
 * There is no bulk dismiss on the service — `read-all` has no counterpart, because a receipt has to
 * be written per notification — so clearing the panel is one request each, a page at a time. The cap
 * is a backstop against looping for ever if the inbox somehow refills as fast as it is emptied; at
 * the current page size it covers 500 notifications, far more than the retention window holds.
 */
const DISMISS_ALL_MAX_ROUNDS = 20;

/** Newest first, which is the order the service returns and the order the panel keeps. */
const byNewest = (a: Notification, b: Notification) => b.id - a.id;

/**
 * What to show the user when a load fails. Everything from fetchData is an ApiError and carries a
 * message worth reading; `fallback` covers anything else that could be thrown.
 */
const messageOf = (error: unknown, fallback: string): string =>
  error instanceof Error && error.message ? error.message : fallback;

/**
 * The bell: what the user has been told, and what they have not read yet.
 *
 * Fed from both ends. The REST inbox is the source of truth and answers on sign-in, on every socket
 * reconnect, and whenever the user pages; the websocket only pushes what has just happened, so the
 * panel is current without polling. The push is at most once by design — the service drops a frame
 * to a slow browser rather than blocking delivery for everyone else — which is exactly why every
 * connect ends in a `catchUp()` against `sinceId`, and why nothing here may treat a missed push as
 * a notification that does not exist.
 *
 * Writes are optimistic and roll back. Marking one read is a receipt on the server and a re-render
 * here, and making the user watch a round trip for that would be the slowest part of the panel.
 */
export const useNotificationStore = create<NotificationStore>((set, get) => ({
  items: [],
  unreadCount: 0,
  isLoaded: false,
  isLoading: false,
  error: null,
  hasMore: false,
  arrivalToken: 0,
  lastArrival: null,

  /**
   * The first page, and the unread count with it. Replaces whatever was held, so it doubles as the
   * repair for a panel that has drifted.
   */
  load: async () => {
    if (get().isLoading) return;
    set({ isLoading: true, error: null });

    try {
      const [items, unreadCount] = await Promise.all([
        NotificationApi.list({ page: 0, size: NOTIFICATION_PAGE_SIZE }),
        NotificationApi.count(true)
      ]);

      set({
        items,
        unreadCount,
        isLoaded: true,
        isLoading: false,
        hasMore: items.length === NOTIFICATION_PAGE_SIZE
      });
    } catch (error) {
      set({ isLoading: false, error: messageOf(error, 'Notifications could not be loaded.') });
    }
  },

  /**
   * The next page.
   *
   * Asks by page index rather than by "older than the oldest id I hold", so a notification pushed
   * while the user is paging shifts the window by one and could hide a row. Tolerated because the
   * alternative is a `beforeId` parameter the service does not have, and because the panel is a
   * reverse-chronological feed rather than a list anybody reconciles — the row is one page further
   * down, not lost, and the next `load()` puts it back where it belongs.
   */
  loadMore: async () => {
    const { isLoading, hasMore, items } = get();
    if (isLoading || !hasMore) return;

    set({ isLoading: true, error: null });
    try {
      const page = Math.floor(items.length / NOTIFICATION_PAGE_SIZE);
      const next = await NotificationApi.list({ page, size: NOTIFICATION_PAGE_SIZE });

      // Deduplicated on the way in: paging by index over a feed that grows at the head can hand
      // back a row already held, and two list items with the same key is a React warning and a
      // panel that looks duplicated.
      const known = new Set(get().items.map((item) => item.id));
      const fresh = next.filter((item) => !known.has(item.id));

      set((state) => ({
        items: [...state.items, ...fresh].sort(byNewest),
        isLoading: false,
        hasMore: next.length === NOTIFICATION_PAGE_SIZE
      }));
    } catch (error) {
      set({ isLoading: false, error: messageOf(error, 'More notifications could not be loaded.') });
    }
  },

  /**
   * Everything that happened while there was no socket.
   *
   * Runs on every connect. The first one has nothing to catch up on and loads the inbox instead;
   * later ones ask only for ids above the highest known, which is cheap enough to do on every
   * reconnect however often the connection flaps. The count is re-read either way, because the
   * same user may have read or dismissed something in another tab.
   */
  catchUp: async () => {
    const { items, isLoaded, load } = get();
    if (!isLoaded) {
      await load();
      return;
    }

    // items is newest first, so the head is the highest id held.
    const sinceId = items[0]?.id ?? null;
    try {
      const [missed, unreadCount] = await Promise.all([
        NotificationApi.list({ sinceId, size: NOTIFICATION_PAGE_SIZE }),
        NotificationApi.count(true)
      ]);

      const known = new Set(get().items.map((item) => item.id));
      const fresh = missed.filter((item) => !known.has(item.id));

      set((state) => ({
        items: fresh.length > 0 ? [...fresh, ...state.items].sort(byNewest) : state.items,
        unreadCount,
        error: null
      }));
    } catch {
      // Left alone deliberately. The panel is still showing everything it had a moment ago, and the
      // next reconnect - or the next time the user opens the bell - tries again. Replacing that with
      // an error banner would report a transient reconnect as a fault.
    }
  },

  /**
   * One notification, pushed. Called from the socket and from nowhere else.
   *
   * Ignores an id already held, which is not a rare case: a push and the `catchUp()` of a
   * reconnection that overlapped it can both deliver the same notification, and counting it twice
   * would leave the badge permanently ahead of the inbox.
   */
  receive: (envelope) => {
    const { items } = get();
    if (items.some((item) => item.id === envelope.id)) return;

    const arrival = notificationFromEnvelope(envelope);
    set((state) => ({
      items: [arrival, ...state.items].sort(byNewest),
      unreadCount: state.unreadCount + 1,
      arrivalToken: state.arrivalToken + 1,
      lastArrival: arrival,
      // A push proves the inbox is reachable even if the first load failed, and leaving isLoaded
      // false would have the panel offer a spinner over a notification it is already showing.
      isLoaded: true
    }));
  },

  markRead: async (id) => {
    const target = get().items.find((item) => item.id === id);
    if (!target || target.read) return;

    set((state) => ({
      items: state.items.map((item) => (item.id === id ? { ...item, read: true } : item)),
      unreadCount: Math.max(0, state.unreadCount - 1)
    }));

    try {
      await NotificationApi.markRead(id);
    } catch {
      set((state) => ({
        items: state.items.map((item) => (item.id === id ? { ...item, read: false } : item)),
        unreadCount: state.unreadCount + 1
      }));
    }
  },

  markUnread: async (id) => {
    const target = get().items.find((item) => item.id === id);
    if (!target || !target.read) return;

    set((state) => ({
      items: state.items.map((item) => (item.id === id ? { ...item, read: false } : item)),
      unreadCount: state.unreadCount + 1
    }));

    try {
      await NotificationApi.markUnread(id);
    } catch {
      set((state) => ({
        items: state.items.map((item) => (item.id === id ? { ...item, read: true } : item)),
        unreadCount: Math.max(0, state.unreadCount - 1)
      }));
    }
  },

  /**
   * Marks the whole inbox read, including the pages the panel has not loaded — which is why the
   * badge goes to zero rather than down by the number of unread rows on screen.
   */
  markAllRead: async () => {
    const previous = get().items;
    const previousUnread = get().unreadCount;
    if (previousUnread === 0 && previous.every((item) => item.read)) return;

    set((state) => ({
      items: state.items.map((item) => (item.read ? item : { ...item, read: true })),
      unreadCount: 0
    }));

    try {
      await NotificationApi.markAllRead();
    } catch {
      set({ items: previous, unreadCount: previousUnread });
    }
  },

  /**
   * Hides one notification from this user.
   *
   * Removed from `items` rather than flagged, because the inbox will not return it again — the
   * service filters dismissed rows out of every list. Nobody else's copy is touched.
   */
  dismiss: async (id) => {
    const target = get().items.find((item) => item.id === id);
    if (!target) return;

    set((state) => ({
      items: state.items.filter((item) => item.id !== id),
      unreadCount: target.read ? state.unreadCount : Math.max(0, state.unreadCount - 1)
    }));

    try {
      await NotificationApi.dismiss(id);
    } catch {
      set((state) => ({
        items: [...state.items, target].sort(byNewest),
        unreadCount: target.read ? state.unreadCount : state.unreadCount + 1
      }));
    }
  },

  /**
   * Empties the panel: dismisses everything the caller can see, page by page.
   *
   * Clears each page optimistically and then sends the requests, so the panel empties at once
   * rather than a row at a time. Anything the service refuses is put back where it was and the loop
   * stops there — carrying on would retry the same failures on the next round and could not
   * terminate.
   *
   * Reloading between rounds is what reaches beyond the page the panel holds: a dismissed
   * notification is gone from every subsequent list, so the same first page comes back holding
   * whatever was behind it.
   */
  dismissAll: async () => {
    for (let round = 0; round < DISMISS_ALL_MAX_ROUNDS; round++) {
      const targets = get().items;
      if (targets.length === 0) return;

      set((state) => ({
        items: [],
        unreadCount: Math.max(0, state.unreadCount - targets.filter((item) => !item.read).length)
      }));

      const outcomes = await Promise.allSettled(targets.map((item) => NotificationApi.dismiss(item.id)));
      const failed = targets.filter((_, index) => outcomes[index]?.status === 'rejected');

      if (failed.length > 0) {
        set((state) => ({
          items: [...state.items, ...failed].sort(byNewest),
          unreadCount: state.unreadCount + failed.filter((item) => !item.read).length,
          error: failed.length === targets.length
            ? 'Nothing could be dismissed. The service may be unreachable.'
            : `${failed.length} of ${targets.length} could not be dismissed.`
        }));
        return;
      }

      // Refills from the service, which both pulls in the next page and puts the unread count
      // back on solid ground after the optimistic arithmetic above.
      await get().load();
    }

    console.warn(
      `[Notifications] Stopped dismissing after ${DISMISS_ALL_MAX_ROUNDS} pages; some remain.`
    );
  },

  reset: () => set({
    items: [],
    unreadCount: 0,
    isLoaded: false,
    isLoading: false,
    error: null,
    hasMore: false,
    lastArrival: null
  })
}));
